import { useCallback, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogPopup,
  DialogTitle,
  Icon,
  Sheet,
  SheetDescription,
  SheetFooter,
  SheetPopup,
  SheetTitle,
  usePresentationFlip,
  type ResizeSize,
} from "@salestrak/ui";
import { IssueMenu } from "#app/components/issue-actions/issue-menu";
import type { IssueRow } from "#app/lib/data";
import { useIssueCardActions } from "#app/hooks/kanban/use-issue-card-actions";
import {
  CreateIssueProvider,
  useCreateIssueMode,
} from "#app/lib/create-issue-context";
import { useItemShareDialogActions } from "#app/lib/item-share-dialog-context";
import { canEdit, canManageSharing } from "#app/lib/utils/access-level";
import { IssueBody } from "./issue-body";
import { useIssueViewDialogState } from "./issue-view-dialog-state";
import { IssueBreadcrumb } from "./issue-view-header";
import {
  readSidebarWidth,
  sidebarWidthBounds,
  writeSidebarWidth,
} from "./use-drawer-width";
import {
  ANCHORED_SHEET_POPUP_CLASS,
  useContentAnchorInsets,
} from "#app/lib/hooks/use-anchor-insets";

// The two desktop presentations are two different Base UI roots (a Sheet and a
// Dialog), so the a11y parts bound to each root differ. The shared header/footer
// take them as input — same parts-record idiom as `IssueMenuParts`.
interface ShellParts {
  Title: typeof DialogTitle | typeof SheetTitle;
  Description: typeof DialogDescription | typeof SheetDescription;
  Footer: typeof DialogFooter | typeof SheetFooter;
}
const DIALOG_PARTS: ShellParts = {
  Title: DialogTitle,
  Description: DialogDescription,
  Footer: DialogFooter,
};
const SHEET_PARTS: ShellParts = {
  Title: SheetTitle,
  Description: SheetDescription,
  Footer: SheetFooter,
};

// Fullscreen keeps a 4rem inset on every side and caps at 1600px.
const FULLSCREEN_POPUP =
  "h-[calc(100dvh-8rem)] w-[calc(100vw-8rem)] max-w-[1600px]";
const SIDEBAR_POPUP = "max-w-none";

const SIDEBAR_MAX_WIDTH = 720;
const FULLSCREEN_MAX_WIDTH = 960;

function IssueViewCornerActions({
  issue,
  desktopSize,
  setDesktopSize,
  onClose,
}: {
  issue: IssueRow;
  desktopSize: "sidebar" | "fullscreen";
  setDesktopSize: (size: "sidebar" | "fullscreen") => void;
  onClose: () => void;
}) {
  const { toggleFavorite, isIssueFavorited } = useIssueCardActions();
  const { openDialog: openShareDialog } = useItemShareDialogActions();
  const isFavorited = isIssueFavorited(issue.issueId);
  // The corner menu lives outside the IssueViewCapabilitiesProvider
  // (which is mounted inside IssueBody), so we read the level
  // directly off the IssueRow's `viewerAccessLevel` field.
  const readOnly = !canEdit(issue.viewerAccessLevel);
  // Only `full_edit` can manage the share list. Mirrors the gate on the
  // three-dot menu's "Sharing and permissions" action.
  const canShare = canManageSharing(issue.viewerAccessLevel);
  return (
    <>
      {canShare && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openShareDialog(issue.issueId)}
        >
          <Icon name="user-plus" size="sm" color="muted" />
          Share
        </Button>
      )}
      <IssueMenu issue={issue} readOnly={readOnly} />
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => toggleFavorite(issue.issueId)}
        aria-label={isFavorited ? "Unfavorite project" : "Favorite project"}
      >
        <Icon
          name="star"
          size="sm"
          className={isFavorited ? "text-warning-foreground" : undefined}
          color={isFavorited ? undefined : "muted"}
        />
      </Button>
      <SizeToggle desktopSize={desktopSize} setDesktopSize={setDesktopSize} />
      <CloseButton onClose={onClose} />
    </>
  );
}

/** The sidebar↔fullscreen flip is an ordinary header button. */
function SizeToggle({
  desktopSize,
  setDesktopSize,
}: {
  desktopSize: "sidebar" | "fullscreen";
  setDesktopSize: (size: "sidebar" | "fullscreen") => void;
}) {
  const toFullscreen = desktopSize === "sidebar";
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={() => setDesktopSize(toFullscreen ? "fullscreen" : "sidebar")}
      aria-label={toFullscreen ? "Expand to fullscreen" : "Shrink to sidebar"}
    >
      <Icon name={toFullscreen ? "maximize" : "minimize-2"} size="sm" />
    </Button>
  );
}

// The stock corner close button is off on both popups (it would collide with
// the in-flow action cluster), so close is an explicit member of the cluster.
function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close">
      <Icon name="x" size="sm" />
    </Button>
  );
}

/** The flush header row: breadcrumb on the left, action cluster on the right,
 *  both in flow rather than absolutely positioned in the corner. */
function IssueViewHeaderRow({
  parts,
  title,
  description,
  breadcrumb,
  actions,
}: {
  parts: ShellParts;
  title: string;
  description: string;
  breadcrumb: React.ReactNode;
  actions: React.ReactNode;
}) {
  const { Title, Description } = parts;
  return (
    <Box
      direction="row"
      align="center"
      gap="sm"
      paddingX="md"
      paddingY="sm"
      shrink={false}
    >
      <Box shrink fill="flex">
        <Title className="sr-only">{title}</Title>
        <Description className="sr-only">{description}</Description>
        {breadcrumb}
      </Box>
      <Box direction="row" align="center" gap="2xs" shrink={false}>
        {actions}
      </Box>
    </Box>
  );
}

export function IssueViewDialog() {
  const dialog = useIssueViewDialogState();

  const viewState = dialog.state?.mode === "view" ? dialog.state : null;
  const createState = dialog.state?.mode === "create" ? dialog.state : null;
  const isOpen = !!dialog.state;

  const isDrawerMode = dialog.desktopSize === "sidebar";

  // Fullscreen↔sidebar continuity: the two presentations are different Base
  // UI roots, so the library FLIP hook morphs the outgoing popup's rect into
  // the incoming one instead of letting the switch pop.
  const popupRef = usePresentationFlip(dialog.desktopSize);
  const propertiesColumns: 1 | 2 =
    dialog.desktopSize === "fullscreen" ? 2 : 1;
  const contentMaxWidth =
    dialog.desktopSize === "fullscreen"
      ? FULLSCREEN_MAX_WIDTH
      : SIDEBAR_MAX_WIDTH;

  const close = useCallback(() => dialog.setState(null), [dialog]);

  const bodyProps = {
    activeTab: dialog.activeTab,
    setActiveTab: dialog.setActiveTab,
    propertiesColumns,
    contentMaxWidth,
    isMobile: false,
  };

  const parts = isDrawerMode ? SHEET_PARTS : DIALOG_PARTS;

  const content = (
    <>
      {viewState && (
        <>
          <IssueViewHeaderRow
            parts={parts}
            title={viewState.issue.name ?? "Issue"}
            description={viewState.issue.issueId}
            breadcrumb={
              <IssueBreadcrumb
                issue={viewState.issue as IssueRow}
              />
            }
            actions={
              <IssueViewCornerActions
                issue={viewState.issue as IssueRow}
                desktopSize={dialog.desktopSize}
                setDesktopSize={dialog.setDesktopSize}
                onClose={close}
              />
            }
          />
          <IssueBody
            {...bodyProps}
            issue={viewState.issue}
            focusNoteId={viewState.focusNoteId}
            focusCustomFieldId={viewState.focusCustomFieldId}
            inlineComposer
          />
        </>
      )}

      {createState && (
        <CreateIssueProvider
          key={
            createState.address?.wholeAddress ??
            (createState.address?.latLng
              ? `${createState.address.latLng.lat},${createState.address.latLng.lng}`
              : "no-address")
          }
          initialWorkflowId={createState.workflowId}
          initialStageId={createState.stageId}
          initialAddress={createState.address}
          initialName={createState.name}
          source={createState.source}
          onCreated={dialog.onCreated}
        >
          <IssueViewHeaderRow
            parts={parts}
            title="Create a new project"
            description="Create a new project"
            breadcrumb={<IssueBreadcrumb />}
            actions={
              <>
                <SizeToggle
                  desktopSize={dialog.desktopSize}
                  setDesktopSize={dialog.setDesktopSize}
                />
                <CloseButton onClose={close} />
              </>
            }
          />
          <IssueBody {...bodyProps} issue={null} />
          <CreateFooter parts={parts} closeIssue={dialog.closeIssue} />
        </CreateIssueProvider>
      )}
    </>
  );

  const onOpenChange = (open: boolean) => {
    if (!open) dialog.setState(null);
  };

  // The sidebar is a non-modal layer: `modal={false}` plus
  // `disablePointerDismissal` leave the page behind it scrollable and
  // interactive.
  if (isDrawerMode) {
    return (
      <Sheet
        open={isOpen}
        onOpenChange={onOpenChange}
        modal={false}
        disablePointerDismissal
      >
        <ResizableSidebarPopup open={isOpen} popupRef={popupRef}>
          {content}
        </ResizableSidebarPopup>
      </Sheet>
    );
  }

  // Fullscreen is a modal layer with a backdrop, and clicking that backdrop
  // closes it — the dialog-system default (pointer dismissal stays on).
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPopup
        className={FULLSCREEN_POPUP}
        ref={popupRef}
        showCloseButton={false}
      >
        {content}
      </DialogPopup>
    </Dialog>
  );
}

/**
 * The right-side sheet with the drag seam on its inner edge. Streamed widths
 * write straight to the element (no state churn per pointermove, matching the
 * particle's contract); the final width persists once on release.
 */
function ResizableSidebarPopup({
  open,
  popupRef,
  children,
}: {
  open: boolean;
  /** Shared popup element ref — the fullscreen↔sidebar FLIP measures it. */
  popupRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) {
  // Pins the sheet inside the content card (below the site header, inside the
  // shell gutter) so it reads as a panel of the page, not a viewport layer.
  const insets = useContentAnchorInsets(open);
  // The viewport is the sheet's container, so its width fixes both the drag
  // bounds and the starting width for this open.
  const [{ bounds, width }] = useState(() =>
    typeof window === "undefined"
      ? { bounds: { max: 0, min: 0 }, width: 0 }
      : {
          bounds: sidebarWidthBounds(window.innerWidth),
          width: readSidebarWidth(window.innerWidth),
        },
  );
  const finalRef = useRef(width);

  // Streamed widths go straight to the element: this popup wraps the entire
  // project view, and a re-render per frame of the drag is not worth paying.
  const onResize = useCallback(({ width: next }: ResizeSize) => {
    finalRef.current = next;
    popupRef.current?.style.setProperty("width", `${next}px`);
  }, [popupRef]);

  const onResizeEnd = useCallback(() => {
    writeSidebarWidth(finalRef.current);
  }, []);

  return (
    <SheetPopup
      className={`${SIDEBAR_POPUP} ${ANCHORED_SHEET_POPUP_CLASS}`}
      ref={popupRef}
      // Non-modal, click-behind panel: the map (and everything else) stays
      // interactive, so no scrim and no blur.
      showBackdrop={false}
      style={{
        marginBottom: insets.bottom,
        marginRight: insets.right,
        marginTop: insets.top,
      }}
      render={
        <Box
          maxWidth={bounds.max || undefined}
          minWidth={bounds.min || undefined}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          resize="left"
          resizeLabel="Resize project panel"
          width={width || undefined}
        />
      }
      showCloseButton={false}
    >
      {children}
    </SheetPopup>
  );
}

function CreateFooter({
  parts,
  closeIssue,
}: {
  parts: ShellParts;
  closeIssue: () => void;
}) {
  const { Footer } = parts;
  const { submit, isSubmitting } = useCreateIssueMode()!;
  return (
    <Footer>
      <Button variant="outline" onClick={closeIssue} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button onClick={submit} disabled={isSubmitting} loading={isSubmitting}>
        Create
      </Button>
    </Footer>
  );
}
